import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getInstitutionShortLabel } from './institutionAssets';
import {
  ClassificationUnavailableExplanation,
  canViewJupasErrorCodes,
  getClassificationNoticeMessage,
  getRequirementFulfillmentDisplay,
  isClassificationUnavailable,
  isRequirementUnavailable,
} from './jupasClassificationDisplay';
import { getSubjectShortLabel } from './dseSubjects';

export function formatStatRow(row) {
  if (!Array.isArray(row) || row.length < 7) return null;
  return {
    year: row[0],
    bandA: row[1],
    bandB: row[2],
    bandC: row[3],
    bandD: row[4],
    bandE: row[5],
    total: row[6],
  };
}

export const displayValue = (value) => (
  value === null || value === undefined || value === '' ? '—' : value
);

export function hasProgrammeBenchmarkScores(upperQuartileScore, medianScore, lowerQuartileScore) {
  return [upperQuartileScore, medianScore, lowerQuartileScore].some(
    (value) => Number.isFinite(Number(value))
  );
}

export function isMissingReferenceScoreError(classificationError) {
  const code = classificationError?.code;
  return code === 'missing_cutoff_scores' || code === 'missing_cutoff_document';
}

export function createNoReferenceScoresFulfillment(newProg = '') {
  return {
    classification: 'CAUTION',
    code: 'C',
    reason_code: 'no_reference_scores',
    message_key: 'no_reference_scores',
    message_params: { new_prog: newProg },
    details: { new_prog: newProg },
  };
}

function formatSubjectLabel(subject, t) {
  if (!subject) return '';
  return getSubjectShortLabel(t, subject);
}

function formatRequirementChoices(requirements, t) {
  if (!requirements || typeof requirements !== 'object') return '';
  return Object.entries(requirements)
    .map(([subject, level]) => `${formatSubjectLabel(subject, t)} ≥ ${level}`)
    .join(' · ');
}

/**
 * Build a human-readable score formula from calc_breakdown, e.g.
 * "中文5.5+英文7×2+數學8.5×2+…".
 */
export function formatScoreCalculationBreakdown(breakdown, t, language = 'en') {
  if (!breakdown || typeof breakdown !== 'object') return '';

  const multiply = language === 'tc' || language === 'sc' ? '*' : '×';
  const parts = [];

  const pushItem = (item, { fractional = false } = {}) => {
    if (!item?.subject) return;
    const label = formatSubjectLabel(item.subject, t);
    const score = Number(item.numeric_score);
    if (!Number.isFinite(score)) return;
    const scoreText = Number.isInteger(score) ? String(score) : String(score);
    const weight = Number(item.weight);
    const fraction = Number(item.fraction);

    if (fractional && Number.isFinite(fraction) && fraction > 0 && fraction < 1) {
      if (Number.isFinite(weight) && weight !== 1) {
        parts.push(`${label}${scoreText}${multiply}${weight}${multiply}${fraction}`);
      } else {
        parts.push(`${label}${scoreText}${multiply}${fraction}`);
      }
      return;
    }

    if (Number.isFinite(weight) && weight !== 1) {
      parts.push(`${label}${scoreText}${multiply}${weight}`);
    } else {
      parts.push(`${label}${scoreText}`);
    }
  };

  (breakdown.compulsory || []).forEach((item) => pushItem(item));
  if (breakdown.optional_1) pushItem(breakdown.optional_1);
  if (breakdown.optional_2) pushItem(breakdown.optional_2);
  (breakdown.free || []).forEach((item) => pushItem(item));
  if (breakdown.fractional_free) pushItem(breakdown.fractional_free, { fractional: true });

  if (parts.length === 0) return '';

  let formula = parts.join('+');
  const hkustBonus = Number(breakdown.adjustments?.hkust_bonus?.bonus);
  if (Number.isFinite(hkustBonus) && hkustBonus !== 0) {
    const bonusText = Number.isInteger(hkustBonus) ? String(hkustBonus) : String(hkustBonus);
    formula += hkustBonus > 0 ? `+${bonusText}` : bonusText;
  }

  return formula;
}

function formatCalculationDetailNumber(value) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return '—';
  return Number.isInteger(numeric) ? String(numeric) : String(Number(numeric.toFixed(2)));
}

function formatCalculationMultiplier(weight, fraction) {
  const numericWeight = Number(weight);
  const numericFraction = Number(fraction);
  if (Number.isFinite(numericFraction) && numericFraction > 0 && numericFraction < 1) {
    const effective = (Number.isFinite(numericWeight) ? numericWeight : 1) * numericFraction;
    return `×${formatCalculationDetailNumber(effective)}`;
  }
  if (!Number.isFinite(numericWeight)) return '—';
  return `×${formatCalculationDetailNumber(numericWeight)}`;
}

function mapBreakdownItemToDetailRow(item, t, { key, fractional = false } = {}) {
  if (!item?.subject) return null;
  const bonusRate = Number(item.weight);
  const multiplier = item.line_type === 'hkust_bonus'
    ? (Number.isFinite(bonusRate) ? `${(bonusRate * 100).toFixed(2)}%` : '—')
    : formatCalculationMultiplier(item.weight, fractional ? item.fraction : undefined);
  return {
    key,
    subjectLabel: formatSubjectLabel(item.subject, t),
    grade: displayValue(item.grade),
    multiplier,
    score: formatCalculationDetailNumber(item.contribution),
  };
}

export function buildScoreCalculationDetailRows(breakdown, t) {
  if (!breakdown || typeof breakdown !== 'object') return [];

  if (Array.isArray(breakdown.lines) && breakdown.lines.length > 0) {
    return breakdown.lines
      .map((item, index) => mapBreakdownItemToDetailRow(item, t, {
        key: `line-${index}`,
        fractional: Number(item.fraction) > 0 && Number(item.fraction) < 1,
      }))
      .filter(Boolean);
  }

  const rows = [];
  (breakdown.compulsory || []).forEach((item, index) => {
    const row = mapBreakdownItemToDetailRow(item, t, { key: `compulsory-${index}` });
    if (row) rows.push(row);
  });

  if (breakdown.optional_1) {
    const row = mapBreakdownItemToDetailRow(breakdown.optional_1, t, { key: 'optional-1' });
    if (row) rows.push(row);
  }

  if (breakdown.optional_2) {
    const row = mapBreakdownItemToDetailRow(breakdown.optional_2, t, { key: 'optional-2' });
    if (row) rows.push(row);
  }

  (breakdown.free || []).forEach((item, index) => {
    const row = mapBreakdownItemToDetailRow(item, t, { key: `free-${index}` });
    if (row) rows.push(row);
  });

  if (breakdown.fractional_free) {
    const row = mapBreakdownItemToDetailRow(
      breakdown.fractional_free,
      t,
      { key: 'fractional-free', fractional: true }
    );
    if (row) rows.push(row);
  }

  return rows;
}

export function hasScoreCalculationDetail(classificationData, t) {
  if (classificationData?.calculation_result?.calc_error) return false;
  return buildScoreCalculationDetailRows(classificationData?.calculation_result?.calc_breakdown, t).length > 0;
}

const SCORE_CALC_DETAIL_GRID_CLASS =
  'grid min-w-0 grid-cols-[minmax(0,1fr)_2rem_2.35rem_2.35rem] items-center gap-x-1 sm:grid-cols-[minmax(0,1.15fr)_3.25rem_3.25rem_3.25rem] sm:gap-x-2';

export function ProgrammeScoreCalculationDetailTable({
  classificationData,
  classificationLoading = false,
  t,
}) {
  if (classificationLoading) {
    return (
      <div className="flex min-h-[2rem] items-center gap-2 text-sm text-ink-subtle" role="status">
        <span className="h-4 w-4 animate-spin rounded-full border-2 border-edge-strong border-t-link" aria-hidden />
        <span>{t('common.loading')}</span>
      </div>
    );
  }

  const calculationResult = classificationData?.calculation_result;
  if (calculationResult?.calc_error) {
    return null;
  }

  const breakdown = calculationResult?.calc_breakdown;
  const rows = buildScoreCalculationDetailRows(breakdown, t);
  if (!rows.length) return null;

  const totalScore = classificationData?.user_score ?? calculationResult?.calc_result;

  return (
    <div className="space-y-2">
      <div className={`${SCORE_CALC_DETAIL_GRID_CLASS} px-2 text-[10px] font-medium leading-tight text-ink-subtle sm:px-3 sm:text-xs`}>
        <span>{t('jupa.scoreCalcDetail.subject')}</span>
        <span className="text-center">{t('jupa.scoreCalcDetail.grade')}</span>
        <span className="text-center">{t('jupa.scoreCalcDetail.multiplier')}</span>
        <span className="text-right">{t('jupa.scoreCalcDetail.score')}</span>
      </div>

      <div className="space-y-1.5">
        {rows.map((row) => (
          <div
            key={row.key}
            className={`${SCORE_CALC_DETAIL_GRID_CLASS} rounded-lg border border-edge bg-surface-muted/70 px-2 py-2 text-[11px] font-medium text-ink sm:rounded-full sm:px-3 sm:text-xs`}
          >
            <span className="truncate" title={row.subjectLabel}>{row.subjectLabel}</span>
            <span className="text-center tabular-nums">{row.grade}</span>
            <span className="text-center tabular-nums text-ink-muted">{row.multiplier}</span>
            <span className="text-right tabular-nums">{row.score}</span>
          </div>
        ))}

        <div
          className={`${SCORE_CALC_DETAIL_GRID_CLASS} rounded-lg border border-edge-strong bg-surface px-2 py-2 text-[11px] font-semibold text-ink sm:rounded-full sm:px-3 sm:text-xs`}
        >
          <span>{t('jupa.scoreCalcDetail.total')}</span>
          <span className="text-center text-ink-subtle">—</span>
          <span className="text-center text-ink-subtle">—</span>
          <span className="text-right text-sm tabular-nums">{formatCalculationDetailNumber(totalScore)}</span>
        </div>
      </div>
    </div>
  );
}

export function formatProgrammeBenchmarkSummary(t, upperQuartileScore, medianScore, lowerQuartileScore, language = 'en') {
  const hasUpperQuartile = Number.isFinite(Number(upperQuartileScore));
  const hasMedian = Number.isFinite(Number(medianScore));
  const hasLowerQuartile = Number.isFinite(Number(lowerQuartileScore));

  const clauses = [];
  if (hasUpperQuartile) {
    clauses.push(t('jupa.scoreBenchmarkUpperQuartileClause', { upperQuartile: upperQuartileScore }));
  }
  if (hasMedian) {
    clauses.push(t('jupa.scoreBenchmarkMedianClause', { median: medianScore }));
  }
  if (hasLowerQuartile) {
    clauses.push(t('jupa.scoreBenchmarkLowerQuartileClause', { lowerQuartile: lowerQuartileScore }));
  }

  if (clauses.length === 0) {
    return t('jupa.scoreBenchmarkUnavailable');
  }

  const isChinese = language === 'tc' || language === 'sc';
  if (isChinese) {
    return `${clauses.join('，')}。`;
  }

  const first = clauses[0].charAt(0).toUpperCase() + clauses[0].slice(1);
  if (clauses.length === 1) {
    return `${first}.`;
  }
  if (clauses.length === 2) {
    return `${first}; ${clauses[1]}.`;
  }
  return `${first}; ${clauses.slice(1, -1).join('; ')}; and ${clauses[clauses.length - 1]}.`;
}

export function ProgrammeBenchmarkScores({
  upperQuartileScore,
  medianScore,
  lowerQuartileScore,
  className = '',
  labelClassName = 'text-xs font-medium text-ink-subtle',
  valueClassName = 'font-semibold tabular-nums text-ink',
}) {
  const items = [
    { label: 'UQ', value: upperQuartileScore },
    { label: 'M', value: medianScore },
    { label: 'LQ', value: lowerQuartileScore },
  ].filter(({ value }) => Number.isFinite(Number(value)));

  if (!items.length) return null;

  return (
    <div className={`flex flex-wrap items-center gap-x-3 gap-y-1 text-sm ${className}`}>
      {items.map(({ label, value }) => (
        <span key={label} className="inline-flex items-baseline gap-1">
          <span className={labelClassName}>{label}</span>
          <span className={valueClassName}>{displayValue(value)}</span>
        </span>
      ))}
    </div>
  );
}

const FUNDING_TRANSLATION_KEYS = {
  'UGC-funded': 'jupa.fundingUgcFunded',
  'Self-financing': 'jupa.fundingSelfFinancing',
  SSSDP: 'jupa.fundingSssdp',
};

export function translateFunding(funding, t) {
  const trimmed = funding?.trim();
  if (!trimmed) return '';
  const key = FUNDING_TRANSLATION_KEYS[trimmed];
  return key ? t(key) : trimmed;
}

export function translateDuration(duration, t, language = 'en') {
  const trimmed = duration?.trim();
  if (!trimmed) return '';
  if (language === 'en') return trimmed;

  let result = trimmed
    .replace(/^Minimum /i, t('jupa.durationMinimum'))
    .replace(/\(Full-time\)/gi, t('jupa.durationFullTime'))
    .replace(/\(summer terms included\)/gi, t('jupa.durationSummerTermsIncluded'))
    .replace(
      /\(including 1 year with common curriculum\)/gi,
      t('jupa.durationIncludingCommonCurriculum')
    )
    .replace(/\(see remark 1\)/gi, t('jupa.durationSeeRemark1'))
    .replace(/(\d+(?:\.\d+)?(?:\s*\+\s*\d+)?)\s*years?/gi, (_, count) =>
      t('jupa.durationYearsCount', { count: count.replace(/\s+/g, '') })
    );

  if (/^\d+$/.test(result)) {
    return t('jupa.durationYearsCount', { count: result });
  }

  return result;
}

export function formatApplyOfferFraction(apply, offer) {
  const applyDisplay = displayValue(apply);
  const offerDisplay = displayValue(offer);

  if (offerDisplay === '—' && applyDisplay === '—') {
    return '—';
  }

  return (
    <>
      <span className="text-green-700 dark:text-green-300">{offerDisplay}</span>
      <span>{' / '}</span>
      <span>{applyDisplay}</span>
    </>
  );
}

export const getInstitutionLabel = (institution, t, language = 'en') =>
  getInstitutionShortLabel(institution, language);

function JupasPageLinkIcon() {
  return (
    <svg className="h-3 w-3" viewBox="0 0 12 12" fill="none" aria-hidden>
      <path
        d="M4.5 2.5H9.5V7.5M9.5 2.5L2.5 9.5"
        stroke="currentColor"
        strokeWidth="1.25"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export function ProgrammeInstitutionLine({
  programme,
  t,
  language = 'en',
  className = '',
}) {
  const institutionLabel = getInstitutionLabel(programme?.institution, t, language);
  const jupasUrl = programme?.url?.trim();

  const handleLinkClick = (event) => {
    event.stopPropagation();
  };

  return (
    <p className={`inline-flex max-w-full items-center gap-0.5 ${className}`}>
      <span>{institutionLabel}</span>
      {jupasUrl ? (
        <a
          href={jupasUrl}
          target="_blank"
          rel="noopener noreferrer"
          onClick={handleLinkClick}
          title={t('jupa.openJupasPage')}
          aria-label={t('jupa.openJupasPage')}
          className="inline-flex shrink-0 items-center justify-center rounded p-0.5 text-ink-subtle transition-colors hover:text-link focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <JupasPageLinkIcon />
        </a>
      ) : null}
    </p>
  );
}

export function getInterviewInfo(programme, t) {
  if (!programme?.interview || programme.interview === 'No') {
    return null;
  }

  if (programme.interview === 'Yes (on a selective basis)') {
    return {
      mainPart: t('jupa.interviewYes'),
      details: t('jupa.interviewSelectiveBasis'),
    };
  }
  if (programme.interview === 'Yes (for all applicants)') {
    return {
      mainPart: t('jupa.interviewYes'),
      details: t('jupa.interviewForAllApplicants'),
    };
  }
  if (programme.interview === 'May require interview and/or test') {
    return {
      mainPart: t('jupa.interviewMayRequireShort'),
      details: t('jupa.interviewMayRequire'),
    };
  }
  if (programme.interview === 'For special cases only') {
    return {
      mainPart: t('jupa.interviewSpecialCasesOnly'),
      details: null,
    };
  }

  const match = programme.interview.match(/^([^(]+)(?:\(([^)]+)\))?$/);
  if (match) {
    const mainPartRaw = match[1].trim();
    const mainPart = mainPartRaw === 'Yes' ? t('jupa.interviewYes') : mainPartRaw;
    const details = match[2] ? match[2].trim() : null;
    return { mainPart, details };
  }

  return { mainPart: programme.interview, details: null };
}

export function getProgrammeMetaParts(programme, t, language = 'en') {
  const parts = [];

  if (programme?.duration?.trim()) {
    parts.push(`${t('jupa.duration')}${translateDuration(programme.duration, t, language)}`);
  }

  const interviewInfo = getInterviewInfo(programme, t);
  if (interviewInfo) {
    const interviewLabel = `${t('jupa.interview')}: ${interviewInfo.mainPart}`;
    parts.push(interviewInfo.details ? `${interviewLabel} (${interviewInfo.details})` : interviewLabel);
  }

  if (programme?.funding?.trim()) {
    parts.push(translateFunding(programme.funding, t));
  }

  return parts;
}

export function getMinimalAdmittedBand(programme) {
  const latestOffer = formatStatRow(programme?.stats_offer?.[0]);
  if (!latestOffer) return '—';

  const bands = [
    ['A', latestOffer.bandA],
    ['B', latestOffer.bandB],
    ['C', latestOffer.bandC],
    ['D', latestOffer.bandD],
    ['E', latestOffer.bandE],
  ];

  let minimalBand = null;
  for (const [band, count] of bands) {
    if (Number(count) > 0) {
      minimalBand = band;
    }
  }

  return minimalBand || '—';
}

/** @deprecated use getMinimalAdmittedBand */
export function getBandAAdmissionRate(programme) {
  const latestApply = formatStatRow(programme?.stats_apply?.[0]);
  const latestOffer = formatStatRow(programme?.stats_offer?.[0]);
  const bandAApply = latestApply?.bandA;
  const bandAOffer = latestOffer?.bandA;

  if (Number(bandAApply) > 0 && Number.isFinite(Number(bandAOffer))) {
    return `${((Number(bandAOffer) / Number(bandAApply)) * 100).toFixed(1)}%`;
  }
  return '—';
}

export function ProgrammeMetaFooter({ programme, t, language = 'en' }) {
  const parts = getProgrammeMetaParts(programme, t, language);
  if (!parts.length) return null;

  return (
    <div className="mt-4 break-words border-t border-edge pt-3 text-xs leading-relaxed text-ink-subtle">
      {parts.join(' · ')}
    </div>
  );
}

function getRequirementReason(fulfillment, t) {
  if (isRequirementUnavailable(fulfillment)) {
    return t('jupa.requirementFulfillment.dataUnavailable');
  }

  const details = fulfillment?.details || {};
  const subjectLabel = formatSubjectLabel(details.subject, t);
  const requirementChoices = formatRequirementChoices(details.requirements, t)
    || details.requirement_choices
    || '';

  switch (fulfillment?.reason_code) {
    case 'score_requirement_not_met': {
      switch (details.requirement_reason_code) {
        case 'missing_compulsory_subject':
          return t('jupa.requirementFulfillment.missingCompulsorySubject', {
            subject: subjectLabel || details.subject,
          });
        case 'compulsory_score_below_required':
          return t('jupa.requirementFulfillment.compulsoryScoreBelowRequired', {
            subject: subjectLabel || details.subject,
            actual: details.actual_score,
            required: details.required_score,
          });
        case 'missing_compulsory_elective':
          return t('jupa.requirementFulfillment.missingCompulsoryElective', {
            required: details.required_score,
          });
        case 'compulsory_elective_below_required':
          return t('jupa.requirementFulfillment.compulsoryElectiveBelowRequired', {
            subject: subjectLabel || details.subject,
            actual: details.actual_score,
            required: details.required_score,
          });
        case 'first_optional_requirement_not_met':
          return t('jupa.requirementFulfillment.firstOptionalRequirementNotMet', {
            requirements: requirementChoices,
          });
        case 'second_optional_requirement_not_met':
          return t('jupa.requirementFulfillment.secondOptionalRequirementNotMet', {
            requirements: requirementChoices,
          });
        default:
          return t('jupa.requirementFulfillment.scoreRequirementNotMet');
      }
    }
    case 'band_requirement_not_met':
      return t('jupa.requirementFulfillment.bandRequirementNotMet', {
        actual: details.actual_band,
        required: details.required_band,
      });
    case 'special_requirement':
      return t('jupa.requirementFulfillment.specialRequirement', {
        requirement: details.special_requirement || fulfillment.reason,
      });
    case 'no_reference_scores':
      return t('jupa.requirementFulfillment.noReferenceScores');
    case 'requirements_met':
      return t('jupa.requirementFulfillment.requirementsMet');
    case 'invalid_scores':
      return t('jupa.requirementFulfillment.invalidScores');
    case 'requirements_unavailable':
      return t('jupa.requirementFulfillment.requirementsUnavailable');
    case 'invalid_ranking':
      return t('jupa.requirementFulfillment.invalidRanking');
    case 'invalid_band_requirement':
      return t('jupa.requirementFulfillment.invalidBandRequirement', {
        requirement: details.band_requirement,
      });
    case 'programme_not_found':
      return t('jupa.requirementFulfillment.programmeNotFound');
    case 'invalid_request':
      return details.missing_fields?.length
        ? t('jupa.requirementFulfillment.invalidRequestFields', {
            fields: details.missing_fields.join(', '),
          })
        : t('jupa.requirementFulfillment.invalidRequest', {
            message: details.server_message,
          });
    case 'service_unavailable':
      return t('jupa.requirementFulfillment.serviceUnavailable', {
        status: details.http_status,
      });
    case 'rate_limited':
      return t('jupa.requirementFulfillment.rateLimited');
    case 'network_error':
      return t('jupa.requirementFulfillment.networkError');
    case 'request_failed':
      return t('jupa.requirementFulfillment.requestFailed', {
        status: details.http_status,
      });
    case 'backend_error':
      return t('jupa.requirementFulfillment.backendError');
    default:
      return t('jupa.requirementFulfillment.evaluationError');
  }
}

export function getRequirementExplanation(fulfillment, t) {
  if (!fulfillment) return null;
  return getRequirementReason(fulfillment, t);
}

function RequirementFulfillmentContent({ fulfillment, t, compact = false }) {
  const display = getRequirementFulfillmentDisplay(fulfillment, t);
  if (!display) return null;

  const reasonText = fulfillment.reason_code !== 'requirements_met'
    ? getRequirementReason(fulfillment, t)
    : null;
  const isWarning = String(fulfillment?.classification || '').toUpperCase() === 'WARNING';

  return (
    <>
      <div className={`flex items-center gap-2 ${compact ? '' : 'mt-1'}`}>
        <span
          className={`inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-bold leading-none ${display.className}`}
          aria-hidden
        >
          {display.code}
        </span>
        <span className={`${compact ? 'text-sm' : ''} font-semibold text-ink`}>{display.label}</span>
      </div>
      {reasonText ? (
        compact ? (
          <p className="mt-1.5 text-xs leading-relaxed text-ink-muted">{reasonText}</p>
        ) : (
          <div
            className={`mt-2 rounded-md px-2.5 py-2 text-xs leading-relaxed ${
              isWarning
                ? 'bg-red-50 text-red-900 dark:bg-red-950/35 dark:text-red-100'
                : 'bg-surface text-ink-muted'
            }`}
          >
            {isWarning ? (
              <p className="mb-1 font-medium">{t('jupa.requirementFulfillment.warningWhy')}</p>
            ) : null}
            <p>{reasonText}</p>
          </div>
        )
      ) : null}
    </>
  );
}

function RequirementFulfillmentRow({ fulfillment, t }) {
  const display = getRequirementFulfillmentDisplay(fulfillment, t);
  if (!display) return null;

  return (
    <div className="col-span-2 border-b-[0.5px] border-edge p-3">
      <p className="text-xs text-ink-subtle">{t('jupa.requirementFulfillment.title')}</p>
      <RequirementFulfillmentContent fulfillment={fulfillment} t={t} />
    </div>
  );
}

export function ProgrammeRequirementNote({ fulfillment, t }) {
  const display = getRequirementFulfillmentDisplay(fulfillment, t);
  if (!display || fulfillment?.reason_code === 'requirements_met') return null;

  return (
    <div className="rounded-md border border-edge bg-surface px-3 py-2.5">
      <p className="text-xs font-medium text-ink-subtle">{t('jupa.requirementFulfillment.title')}</p>
      <RequirementFulfillmentContent fulfillment={fulfillment} t={t} compact />
    </div>
  );
}

export function ProgrammeMetaList({ programme, t, language = 'en', compact = false }) {
  const rows = [];

  if (programme?.duration?.trim()) {
    rows.push({
      label: t('jupa.duration').replace(/:\s*$/, ''),
      value: translateDuration(programme.duration, t, language),
    });
  }

  const interviewInfo = getInterviewInfo(programme, t);
  if (interviewInfo) {
    rows.push({
      label: t('jupa.interview'),
      value: interviewInfo.details
        ? `${interviewInfo.mainPart} (${interviewInfo.details})`
        : interviewInfo.mainPart,
    });
  }

  if (programme?.funding?.trim()) {
    rows.push({
      label: t('jupa.fundingLabel'),
      value: translateFunding(programme.funding, t),
    });
  }

  const institutionLabel = getInstitutionLabel(programme?.institution, t, language);
  if (institutionLabel) {
    rows.unshift({
      label: t('jupa.institutionLabel'),
      value: institutionLabel,
    });
  }

  if (!rows.length) return null;

  return (
    <dl className={`space-y-2 sm:grid sm:grid-cols-[minmax(4.5rem,auto)_minmax(0,1fr)] sm:gap-x-3 sm:space-y-0 ${compact ? 'sm:gap-y-2.5 sm:text-sm' : 'sm:gap-y-2.5 sm:text-xs'}`}>
      {rows.map(({ label, value }) => (
        <div
          key={label}
          className="grid min-w-0 grid-cols-[4.5rem_minmax(0,1fr)] gap-x-2 rounded-lg bg-surface-muted/55 px-3 py-2.5 sm:contents"
        >
          <dt className={compact ? 'text-[11px] text-ink-subtle sm:text-xs' : 'text-xs text-ink-subtle'}>{label}</dt>
          <dd className={`min-w-0 break-words leading-snug text-ink ${compact ? 'text-sm font-medium' : 'text-xs font-medium leading-relaxed'}`}>{value}</dd>
        </div>
      ))}
    </dl>
  );
}

export function ProgrammeStatsGrid({
  programme,
  classificationData,
  t,
  year,
  includeRequirementRow = true,
  compact = false,
  borderless = false,
}) {
  const latestApply = formatStatRow(programme?.stats_apply?.[0]);
  const latestOffer = formatStatRow(programme?.stats_offer?.[0]);
  const bandAApply = latestApply?.bandA;
  const bandBApply = latestApply?.bandB;
  const bandAOffer = latestOffer?.bandA;
  const bandBOffer = latestOffer?.bandB;
  const minimalAdmittedBand = getMinimalAdmittedBand(programme);
  const intakeLabel = year ? t('jupa.intakeYearLabel', { year }) : t('jupa.intakeLabel');

  const cellClass = compact ? 'py-1' : 'p-3';
  const labelClass = 'text-xs text-ink-subtle';
  const valueClass = compact ? 'mt-0.5 text-xs font-semibold text-ink' : 'mt-1 text-sm font-semibold text-ink';

  if (borderless) {
    const borderlessCellClass = compact ? 'p-0 sm:px-3 sm:py-2.5' : 'p-0 sm:p-3';
    const borderlessValueClass = 'mt-0.5 text-xs font-semibold text-ink';
    const mobileStatCellClass =
      'min-w-0 rounded-lg bg-surface-muted/55 px-2.5 py-2.5 sm:rounded-none sm:bg-transparent sm:p-0';

    return (
      <div className="overflow-hidden rounded-lg bg-transparent text-xs sm:border sm:border-edge sm:bg-surface">
        <div className={`grid grid-cols-2 gap-2 sm:gap-x-4 sm:gap-y-3 ${borderlessCellClass}`}>
          {includeRequirementRow ? (
            <RequirementFulfillmentRow
              fulfillment={classificationData?.requirement_fulfillment}
              t={t}
            />
          ) : null}
          <div className={mobileStatCellClass}>
            <p className={labelClass}>{t('jupa.bandAApplyToOffer')}</p>
            <p className={borderlessValueClass}>
              {formatApplyOfferFraction(bandAApply, bandAOffer)}
            </p>
          </div>
          <div className={mobileStatCellClass}>
            <p className={labelClass}>{t('jupa.bandBApplyToOffer')}</p>
            <p className={borderlessValueClass}>
              {formatApplyOfferFraction(bandBApply, bandBOffer)}
            </p>
          </div>
          <div className={mobileStatCellClass}>
            <p className={labelClass}>{intakeLabel}</p>
            <p className={borderlessValueClass}>{displayValue(programme?.intake)}</p>
          </div>
          <div className={mobileStatCellClass}>
            <p className={labelClass}>{t('jupa.minimalAdmittedBandLabel')}</p>
            <p className={borderlessValueClass}>{minimalAdmittedBand}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`grid grid-cols-2 overflow-hidden rounded-lg border border-edge bg-surface ${compact ? 'text-xs' : 'text-sm'}`}>
      {includeRequirementRow ? (
        <RequirementFulfillmentRow
          fulfillment={classificationData?.requirement_fulfillment}
          t={t}
        />
      ) : null}
      <div className={`border-b-[0.5px] border-r-[0.5px] border-edge ${cellClass}`}>
        <p className={labelClass}>{t('jupa.bandAApplyToOffer')}</p>
        <p className={valueClass}>
          {formatApplyOfferFraction(bandAApply, bandAOffer)}
        </p>
      </div>
      <div className={`border-b-[0.5px] border-edge ${cellClass}`}>
        <p className={labelClass}>{t('jupa.bandBApplyToOffer')}</p>
        <p className={valueClass}>
          {formatApplyOfferFraction(bandBApply, bandBOffer)}
        </p>
      </div>
      <div className={`border-r-[0.5px] border-edge ${cellClass}`}>
        <p className={labelClass}>{intakeLabel}</p>
        <p className={valueClass}>{displayValue(programme?.intake)}</p>
      </div>
      <div className={cellClass}>
        <p className={labelClass}>{t('jupa.minimalAdmittedBandLabel')}</p>
        <p className={valueClass}>{minimalAdmittedBand}</p>
      </div>
    </div>
  );
}

export function ProgrammeScoreSummary({
  programme,
  classificationData,
  t,
  language = 'en',
  scoreDataPending = false,
}) {
  const [breakdownOpen, setBreakdownOpen] = useState(false);
  const { currentUser } = useAuth();
  const showErrorCode = canViewJupasErrorCodes(currentUser);
  const classificationError = classificationData?.classification_error;
  const classificationNotice = classificationData?.classification_notice;
  const classificationNoticeMessage = getClassificationNoticeMessage(classificationNotice, t);
  const userScore = classificationData?.user_score;
  const upperQuartileScore = programme?.score_uq ?? classificationData?.score_uq;
  const medianScore = programme?.score_md ?? classificationData?.score_md;
  const lowerQuartileScore = programme?.score_lq ?? classificationData?.score_lq;
  const hasUserScore = userScore !== null
    && userScore !== undefined
    && Number.isFinite(Number(userScore));
  const hasBenchmarks = hasProgrammeBenchmarkScores(
    upperQuartileScore,
    medianScore,
    lowerQuartileScore
  );
  const benchmarkSummary = formatProgrammeBenchmarkSummary(
    t,
    upperQuartileScore,
    medianScore,
    lowerQuartileScore,
    language
  );
  const hasScoreComparison = hasUserScore && Number.isFinite(Number(medianScore));
  const isAboveMedian = hasScoreComparison && Number(userScore) >= Number(medianScore);
  const calcBreakdown = classificationData?.calculation_result?.calc_breakdown;
  const scoreFormula = formatScoreCalculationBreakdown(calcBreakdown, t, language);

  if (classificationError || isClassificationUnavailable(classificationData?.classification)) {
    return (
      <div className="rounded-lg border border-edge bg-surface-muted/60 p-3">
        <ClassificationUnavailableExplanation
          error={classificationError}
          classification={classificationData?.classification}
          showErrorCode={showErrorCode}
          t={t}
        />
      </div>
    );
  }

  if (hasUserScore) {
    return (
      <div className="space-y-3">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="flex items-end gap-2">
              <span className="text-[28px] font-bold leading-none text-ink">
                {displayValue(userScore)}
              </span>
              <span className="pb-1 text-xs text-ink-subtle">{t('jupa.yourCalculatedScore')}</span>
            </div>
            <p className="mt-2 text-xs leading-relaxed text-ink-subtle">
              {benchmarkSummary}
            </p>
            {classificationNoticeMessage ? (
              <p className="mt-2 rounded-md bg-amber-50 px-2.5 py-2 text-xs leading-relaxed text-amber-900 dark:bg-amber-950/40 dark:text-amber-100">
                {classificationNoticeMessage}
              </p>
            ) : null}
          </div>
          {hasScoreComparison ? (
            <span className={`shrink-0 rounded-full px-3 py-1 text-xs font-medium ${
              isAboveMedian
                ? 'bg-[#f0fdf4] text-[#15803d] dark:bg-emerald-950/60 dark:text-emerald-200'
                : 'bg-[#fff7ed] text-[#c2410c] dark:bg-orange-950/50 dark:text-orange-200'
            }`}>
              {isAboveMedian ? t('jupa.aboveMedian') : t('jupa.belowMedian')}
            </span>
          ) : null}
        </div>

        {scoreFormula ? (
          <div className="rounded-lg border border-edge bg-surface-muted/60 px-3 py-2.5">
            <button
              type="button"
              onClick={() => setBreakdownOpen((open) => !open)}
              aria-expanded={breakdownOpen}
              className="flex w-full items-center justify-between gap-2 text-left text-xs font-semibold text-ink hover:text-link focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <span>{t('jupa.scoreCalculationBreakdown')}</span>
              <svg
                className={`h-4 w-4 shrink-0 transition-transform duration-200 ${breakdownOpen ? 'rotate-180' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                aria-hidden
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            {breakdownOpen ? (
              <p className="mt-2 break-words font-mono text-sm leading-relaxed text-ink">
                {scoreFormula}
                <span className="text-ink-muted"> = {displayValue(userScore)}</span>
              </p>
            ) : (
              <p className="mt-1.5 truncate font-mono text-xs text-ink-muted" title={scoreFormula}>
                {scoreFormula}
              </p>
            )}
          </div>
        ) : null}
      </div>
    );
  }

  if (hasBenchmarks) {
    return (
      <ProgrammeBenchmarkScores
        upperQuartileScore={upperQuartileScore}
        medianScore={medianScore}
        lowerQuartileScore={lowerQuartileScore}
      />
    );
  }

  if (scoreDataPending) {
    return <p className="text-sm text-ink-subtle">{t('jupa.scoreDataPendingRelease')}</p>;
  }

  return null;
}
